/**
 * Event Timestamp Component for Timeline V2
 *
 * Displays timestamps in relative or absolute format.
 * Right-aligned using semantic color tokens for dark mode support.
 *
 * Format (from Figma):
 * - Relative: "1hr ago", "2 days ago", "just now"
 * - Absolute: "Jan 15, 2025 at 3:45 PM"
 * - Font: 12px (text-xs)
 * - Color: text-secondary (compatibility layer token mapped in design-system.css)
 */

import { useEffect, useState } from 'react';

import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import { type TimeFormat, timeParts, useOrgTimeFormat } from '@/lib/time';

import { TIMELINE_DESIGN_TOKENS } from '../../design-tokens';
import type { EventTimestampProps } from '../../types/renderer';

/**
 * Event timestamp component
 *
 * Shows relative time by default (e.g., "2 hours ago")
 */
export function EventTimestamp({
  timestamp,
  format = 'relative',
  override,
}: EventTimestampProps) {
  const { timeFormat } = useOrgTimeFormat();
  const [, setTick] = useState(0);

  // Auto-update relative time display every 15 seconds
  useEffect(() => {
    if (override || format !== 'relative') {
      return;
    }

    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 15000);

    return () => clearInterval(interval);
  }, [override, format]);

  // Use override if provided
  if (override) {
    return <time className={getTimestampClassName()}>{override}</time>;
  }

  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;

  // Validate date
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  // Format timestamp
  const formattedTime =
    format === 'relative'
      ? formatRelativeTimeShort(date)
      : formatAbsoluteTime(date, timeFormat);

  return (
    <Tooltip title={formatAbsoluteTime(date, timeFormat)}>
      <TooltipTrigger>
        <time className={getTimestampClassName()} dateTime={date.toISOString()}>
          {formattedTime}
        </time>
      </TooltipTrigger>
    </Tooltip>
  );
}

/**
 * Get timestamp className
 *
 * Uses semantic color token for dark mode support
 */
function getTimestampClassName(): string {
  return `${TIMELINE_DESIGN_TOKENS.typography.timestamp} flex-shrink-0 text-secondary`;
}

/**
 * Format relative time in short format (Figma style)
 * "1hr ago", "2d ago", "just now"
 */
function formatRelativeTimeShort(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);

  if (diffSec < 60) {
    return 'just now';
  }
  if (diffMin < 60) {
    return `${diffMin}m ago`;
  }
  if (diffHour < 24) {
    return `${diffHour}hr ago`;
  }
  if (diffDay < 7) {
    return `${diffDay}d ago`;
  }
  if (diffDay < 30) {
    return `${diffWeek}w ago`;
  }
  if (diffDay < 365) {
    return `${diffMonth}mo ago`;
  }
  return `${diffYear}y ago`;
}

/**
 * Format as absolute date/time
 */
function formatAbsoluteTime(date: Date, timeFormat: TimeFormat): string {
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...timeParts(timeFormat),
  });
}
